import React, { useState, useEffect } from 'react';
import { Lock, Mail, Loader2 } from 'lucide-react';

const AcceptInvitePage = ({ token, onAccept }) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [email, setEmail] = useState('');
  const [valid, setValid] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setValid(false);
      setLoading(false);
      return;
    }
    const validate = async () => {
      try {
        const { api } = await import('../services/api.js');
        const res = await api.invites.validate(token);
        if (res.valid) {
          setEmail(res.email || '');
          setValid(true);
        } else {
          setError(res.error || 'Invalid or expired invite.');
          setValid(false);
        }
      } catch (e) {
        setError('Could not verify invite link.');
        setValid(false);
      } finally {
        setLoading(false);
      }
    };
    validate();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setSubmitting(true);
    try {
      const { api } = await import('../services/api.js');
      const res = await api.auth.acceptInvite(token, password);
      if (res.token && res.user) {
        if (typeof window !== 'undefined' && window.localStorage) {
          window.localStorage.setItem('marketreachToken', res.token);
          window.localStorage.setItem('marketreachUser', JSON.stringify(res.user));
          window.localStorage.setItem('marketreachAuth', 'true');
        }
        if (typeof onAccept === 'function') {
          onAccept(res.user);
        }
        // Leave /accept-invite so refresh shows the app, not the invite page
        window.history.replaceState({}, '', '/');
      }
    } catch (err) {
      setError(err?.payload?.error || err?.message || 'Something went wrong.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center px-4 py-12">
        <div className="flex flex-col items-center gap-4 text-gray-600">
          <Loader2 className="animate-spin" size={40} />
          <p>Checking invite link...</p>
        </div>
      </div>
    );
  }

  if (!valid) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center px-4 py-12">
        <div className="w-full max-w-md rounded-2xl border border-red-100 bg-white p-10 shadow-xl text-center">
          <h2 className="text-xl font-bold text-gray-900">Invalid invite</h2>
          <p className="mt-2 text-gray-600">{error}</p>
          <a
            href="/"
            className="mt-6 inline-block rounded-lg bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            Back to sign in
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-md rounded-2xl border border-indigo-100 bg-white p-10 shadow-xl">
        <div className="mb-8 text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 text-white shadow-lg">
            <Mail size={28} />
          </div>
          <h2 className="mt-6 text-2xl font-bold text-gray-900">Set your password</h2>
          <p className="mt-2 text-sm text-gray-500">
            You're joining as <strong>{email}</strong>. Choose a password to finish.
          </p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700">Password</label>
            <div className="mt-2 relative">
              <Lock className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-indigo-400" size={18} />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-lg border border-gray-200 pl-10 pr-4 py-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                placeholder="At least 8 characters"
                autoComplete="new-password"
                required
                minLength={8}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700">Confirm password</label>
            <div className="mt-2 relative">
              <Lock className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-indigo-400" size={18} />
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full rounded-lg border border-gray-200 pl-10 pr-4 py-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                placeholder="Confirm password"
                autoComplete="new-password"
                required
                minLength={8}
              />
            </div>
          </div>
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 px-4 py-3 text-sm font-semibold text-white shadow-lg transition hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/40 disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <Loader2 className="animate-spin" size={18} />
                Creating account...
              </>
            ) : (
              'Create account & sign in'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AcceptInvitePage;
